/**
 * Admin panel frame — same "Aurora" look as the web app (D49): a top bar with round icon
 * buttons (lime = current page). On phones the icons scroll sideways under the logo.
 */
import type { AdminPermission } from "@custos/core";
import { Badge, Logo, NetworkBadge, cn } from "@custos/ui";
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  BarChart3,
  CircleUser,
  LayoutDashboard,
  LogOut,
  Megaphone,
  MessagesSquare,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, useLocation, useNavigate } from "react-router";
import { api, queryClient } from "../lib/api";
import { useCan } from "../lib/permissions";
import { useConfig, useOverview } from "../lib/queries";

/** Pages and the permissions that open them (any one is enough; empty = everyone). Shared with App routes. */
export const NAV: { to: string; label: string; icon: LucideIcon; perms: AdminPermission[] }[] = [
  { to: "/", label: "Overview", icon: LayoutDashboard, perms: ["dashboard.view"] },
  { to: "/withdrawals", label: "Withdrawals", icon: ArrowUpRight, perms: ["queues.view"] },
  { to: "/deposits", label: "Deposits", icon: ArrowDownLeft, perms: ["queues.view"] },
  { to: "/swaps", label: "Swaps", icon: ArrowLeftRight, perms: ["swaps.view"] },
  { to: "/users", label: "Users", icon: Users, perms: ["users.view"] },
  { to: "/wallets", label: "Wallets", icon: Wallet, perms: ["wallets.view"] },
  {
    to: "/vault-recovery",
    label: "Vault recovery",
    icon: ShieldCheck,
    perms: ["vault_recovery.view", "vault_recovery.decide"],
  },
  {
    to: "/limits",
    label: "Limits",
    icon: SlidersHorizontal,
    perms: ["dashboard.view", "emergency.pause", "limits.edit"],
  },
  { to: "/audit", label: "Audit log", icon: ScrollText, perms: ["audit.view"] },
  { to: "/reports", label: "Reports", icon: BarChart3, perms: ["reports.view"] },
  { to: "/support", label: "Support", icon: MessagesSquare, perms: ["support.chat"] },
  { to: "/messages", label: "Messages", icon: Megaphone, perms: ["messages.manage"] },
  { to: "/staff", label: "Staff", icon: UserCog, perms: ["staff.manage"] },
  { to: "/account", label: "My account", icon: CircleUser, perms: [] },
];

export function useLogout() {
  const navigate = useNavigate();
  return async () => {
    await api.logout();
    queryClient.clear();
    navigate("/login", { replace: true });
  };
}

export function AdminShell({ children }: { children: ReactNode }) {
  const config = useConfig();
  const can = useCan();
  const overview = useOverview(can("dashboard.view"));
  const nav = NAV.filter((item) => item.perms.length === 0 || can(...item.perms));
  // Conversations with unread customer messages (D79), every 30 s.
  const supportUnread = useQuery({
    queryKey: ["support-unread"],
    queryFn: () => api.supportUnread(),
    refetchInterval: 30_000,
    enabled: can("support.chat"),
  });
  const logout = useLogout();
  const location = useLocation();
  const pending = overview.data?.pendingApproval ?? 0;

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-30 bg-bg/75 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6 lg:flex-nowrap lg:px-10 lg:py-4">
          <div className="flex items-center gap-2">
            <Logo />
            <Badge tone="primary">Admin</Badge>
          </div>
          <div className="ml-auto flex items-center gap-2 lg:order-3 lg:ml-0">
            {config.data && <NetworkBadge label={config.data.badge} />}
            <button
              type="button"
              onClick={logout}
              title="Log out"
              aria-label="Log out"
              className="inline-flex size-11 items-center justify-center rounded-full bg-soft text-fg hover:bg-hover"
            >
              <LogOut className="size-5" aria-hidden />
            </button>
          </div>
          <nav
            aria-label="Admin navigation"
            data-testid="adminnav"
            className="-mx-4 flex w-[calc(100%+2rem)] gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:w-full sm:px-0 lg:order-2 lg:mx-auto lg:w-auto lg:pb-0"
          >
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                title={item.label}
                aria-label={item.label}
                className={({ isActive }) =>
                  cn(
                    "relative inline-flex size-11 shrink-0 items-center justify-center rounded-full transition-colors duration-200 lg:size-12",
                    isActive ? "animate-pop bg-primary text-on-primary" : "bg-soft text-fg hover:bg-hover",
                  )
                }
              >
                <item.icon className="size-5" aria-hidden />
                {item.to === "/support" && (supportUnread.data ?? 0) > 0 && (
                  <span
                    data-testid="support-unread"
                    className="absolute -top-0.5 -right-0.5 inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-on-primary"
                  >
                    {supportUnread.data}
                  </span>
                )}
                {item.to === "/withdrawals" && pending > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 inline-flex min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-[#0B0A10]">
                    {pending}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      {overview.data?.withdrawalsPaused && (
        <div role="alert" className="bg-danger/15 px-4 py-2 text-center text-sm font-medium text-danger">
          ALL WITHDRAWALS ARE PAUSED
        </div>
      )}

      <main
        key={location.pathname}
        className="mx-auto w-full max-w-7xl flex-1 animate-fade px-4 pt-4 pb-16 sm:px-6 lg:px-10"
      >
        {children}
      </main>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3 sm:mb-6">
      <div className="min-w-0">
        <h1 className="font-heading text-[28px] leading-tight font-medium text-fg sm:text-4xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
